/**
 * AP-34 12. — **Offene Posten und Zahlung** als reine Funktionen: Zahlstatus, Skonto,
 * Alterssummen, IBAN-Prüfung, die SEPA-Überweisungsdatei (`pain.001`) und das Lesen eines
 * Kontoauszugs (`camt.053`).
 */
import type { IncomingPayStatus } from './incoming-invoices'

const cent = (n: number) => Math.round(n * 100)
const euro = (c: number) => c / 100

// ── Zahlstatus und Skonto ───────────────────────────────────────────────────

/**
 * Zahlstatus aus Betrag, Bezahltem und Fälligkeit. Vor der Freigabe ist nichts zahlbar (`NONE`);
 * offen bleibt, was laut Rechnung zu zahlen ist, abzüglich Zahlungen und Skonto.
 */
export function zahlstatus(o: { brutto: number; bezahlt: number; faellig: string | null; heute: string; zahlbar: boolean }): { status: IncomingPayStatus; offen: number } {
  const offen = euro(cent(o.brutto) - cent(o.bezahlt))
  if (!o.zahlbar) return { status: 'NONE', offen }
  // Eine Gutschrift (negativer Betrag) ist ausgeglichen, sobald nichts mehr offen ist — in beide Richtungen
  if (cent(offen) === 0 || (o.brutto > 0 && offen < 0) || (o.brutto < 0 && offen > 0)) return { status: 'PAID', offen }
  if (cent(o.bezahlt) !== 0) return { status: 'PARTIALLY_PAID', offen }
  if (o.faellig && o.faellig < o.heute) return { status: 'OVERDUE', offen }
  return { status: 'OPEN', offen }
}

/** Skonto auf den offenen Betrag, kaufmännisch gerundet. */
export function skontoBetrag(offen: number, prozent: number | null | undefined): number {
  if (!prozent || offen <= 0) return 0
  return euro(Math.round((cent(offen) * prozent) / 100))
}

/** Skonto gilt, wenn die Frist am Ausführungstag noch nicht abgelaufen ist (12.3-2). */
export function skontoGilt(skontoBis: string | null | undefined, ausfuehrung: string): boolean {
  return !!skontoBis && skontoBis >= ausfuehrung
}

export const INCOMING_AGE_BUCKETS = ['NOT_DUE', 'D1_30', 'D31_60', 'OVER_60'] as const
export type IncomingAgeBucket = (typeof INCOMING_AGE_BUCKETS)[number]

const tageZwischen = (von: string, bis: string) => Math.round((Date.parse(`${bis}T00:00:00Z`) - Date.parse(`${von}T00:00:00Z`)) / 86_400_000)

/** 12.1 — Alterssummen: nicht fällig · 1–30 · 31–60 · über 60 Tage überfällig. Ohne Fälligkeit zählt „nicht fällig". */
export function eingangAlterssummen(posten: readonly { offen: number; faellig: string | null }[], heute: string): Record<IncomingAgeBucket, number> {
  const c: Record<IncomingAgeBucket, number> = { NOT_DUE: 0, D1_30: 0, D31_60: 0, OVER_60: 0 }
  for (const p of posten) {
    const tage = p.faellig ? tageZwischen(p.faellig, heute) : 0
    const topf: IncomingAgeBucket = tage <= 0 ? 'NOT_DUE' : tage <= 30 ? 'D1_30' : tage <= 60 ? 'D31_60' : 'OVER_60'
    c[topf] += cent(p.offen)
  }
  return { NOT_DUE: euro(c.NOT_DUE), D1_30: euro(c.D1_30), D31_60: euro(c.D31_60), OVER_60: euro(c.OVER_60) }
}

// ── IBAN und SEPA-Zeichensatz ───────────────────────────────────────────────

/**
 * Die IBAN in ihrer Schreibweise ohne Gruppen: nur Großbuchstaben und Ziffern.
 *
 * Seit Nachtrag 6 (PT-24) fällt alles weg, was nicht zur IBAN gehört — nicht nur Leerraum: Punkte und
 * Bindestriche zwischen den Gruppen, unsichtbare Zeichen aus der Zwischenablage (aus einem PDF oder dem
 * Onlinebanking kopiert, sieht die IBAN richtig aus und ist es nicht) und ein vorangestelltes „IBAN". Vorher
 * galt eine solche Eingabe als ungültig, und der Zahlungslauf meldete, die IBAN des Betriebs fehle.
 */
export const ibanNormal = (iban: string | null | undefined): string =>
  (iban ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    // „IBAN AT61 …": Kein Land heißt IB, und nach dem Länderkürzel folgen zwei Ziffern.
    .replace(/^IBAN(?=[A-Z]{2}\d{2})/, '')

/** Eine IBAN in Vierergruppen, wie sie auf dem Papier steht. */
export const ibanText = (iban: string | null | undefined): string => ibanNormal(iban).replace(/(.{4})(?=.)/g, '$1 ')

/** Was mit einer eingetragenen IBAN ist: nichts da, nicht gültig (Aufbau oder Prüfziffer) oder in Ordnung. */
export type IbanStand = 'MISSING' | 'INVALID' | 'OK'
export const ibanStand = (iban: string | null | undefined): IbanStand => (!(iban ?? '').trim() ? 'MISSING' : ibanGueltig(iban) ? 'OK' : 'INVALID')

/** Prüfziffer nach ISO 13616 (Modulo 97). Die Länge je Land prüft die Bank. */
export function ibanGueltig(iban: string | null | undefined): boolean {
  const s = ibanNormal(iban)
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(s)) return false
  const umgestellt = s.slice(4) + s.slice(0, 4)
  let rest = 0
  for (const z of umgestellt) {
    const wert = z >= 'A' ? String(z.charCodeAt(0) - 55) : z
    for (const ziffer of wert) rest = (rest * 10 + Number(ziffer)) % 97
  }
  return rest === 1
}

const UMLAUTE: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', Ä: 'Ae', Ö: 'Oe', Ü: 'Ue', ß: 'ss', '&': '+', é: 'e', è: 'e', ê: 'e', á: 'a', à: 'a', â: 'a', ó: 'o', ò: 'o', ô: 'o', ú: 'u', ù: 'u', û: 'u', í: 'i', ì: 'i', ç: 'c', ñ: 'n', š: 's', č: 'c', ž: 'z', ć: 'c', đ: 'd', ł: 'l', '€': 'EUR', '–': '-', '—': '-', '„': '"', '“': '"', '”': '"', '’': "'" }

/**
 * Text im SEPA-Zeichensatz: Umlaute transliteriert, alles Unbekannte ein Leerzeichen, auf die
 * Länge gekürzt. Banken weisen eine Datei mit einem einzigen fremden Zeichen als Ganzes ab.
 */
export function sepaText(text: string | null | undefined, laenge: number): string {
  const t = [...(text ?? '')].map((z) => UMLAUTE[z] ?? z).join('')
  return t
    .replace(/[^A-Za-z0-9/\-?:().,'+ ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, laenge)
    .trim()
}

/** 12.3-4 — `ER-` + Eingangsnummer + laufende Nummer; höchstens 35 Zeichen. */
export function endToEndId(eingangsnummer: string, laufend: number): string {
  const nr = eingangsnummer.replace(/[^A-Za-z0-9]/g, '')
  return `ER-${nr}-${laufend}`.slice(0, 35)
}

// ── pain.001 ────────────────────────────────────────────────────────────────

export const SEPA_SCHEMAS = ['pain.001.001.09', 'pain.001.001.03'] as const
export type SepaSchema = (typeof SEPA_SCHEMAS)[number]

export interface SepaPosten {
  endToEndId: string
  betrag: number
  empfaenger: string
  iban: string
  bic?: string | null
  verwendungszweck: string
}

export interface SepaAuftrag {
  schema: SepaSchema
  /** eindeutig je Datei, höchstens 35 Zeichen */
  nachrichtId: string
  /** ISO-Zeitpunkt der Erstellung */
  erstelltAm: string
  /** ISO-Datum */
  ausfuehrung: string
  auftraggeber: string
  iban: string
  bic?: string | null
  posten: readonly SepaPosten[]
}

const x = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
const betragText = (n: number) => (cent(n) / 100).toFixed(2)

/**
 * 12.3-4 — die SEPA-Überweisungsdatei: ein Sammelauftrag je Ausführungsdatum, Batch-Booking an.
 * Wirft bei einem Posten, den keine Bank annimmt (Betrag ≤ 0, ungültige IBAN) — die Datei entsteht
 * ganz oder gar nicht.
 */
export function pain001(a: SepaAuftrag): string {
  if (!a.posten.length) throw new Error('SEPA_EMPTY')
  if (!ibanGueltig(a.iban)) throw new Error('SEPA_DEBTOR_IBAN')
  for (const p of a.posten) {
    if (cent(p.betrag) <= 0) throw new Error(`SEPA_AMOUNT:${p.endToEndId}`)
    if (!ibanGueltig(p.iban)) throw new Error(`SEPA_CREDITOR_IBAN:${p.endToEndId}`)
  }
  const v9 = a.schema === 'pain.001.001.09'
  const bicTag = v9 ? 'BICFI' : 'BIC'
  const summe = betragText(euro(a.posten.reduce((s, p) => s + cent(p.betrag), 0)))
  const anzahl = String(a.posten.length)
  const name = x(sepaText(a.auftraggeber, 70))
  const bank = (bic: string | null | undefined, pflicht: boolean) =>
    bic?.trim() ? `<FinInstnId><${bicTag}>${x(bic.replace(/\s/g, '').toUpperCase())}</${bicTag}></FinInstnId>` : pflicht ? '<FinInstnId><Othr><Id>NOTPROVIDED</Id></Othr></FinInstnId>' : ''
  const zeilen: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<Document xmlns="urn:iso:std:iso:20022:tech:xsd:${a.schema}" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">`,
    ' <CstmrCdtTrfInitn>',
    '  <GrpHdr>',
    `   <MsgId>${x(sepaText(a.nachrichtId, 35))}</MsgId>`,
    `   <CreDtTm>${x(a.erstelltAm.slice(0, 19))}</CreDtTm>`,
    `   <NbOfTxs>${anzahl}</NbOfTxs>`,
    `   <CtrlSum>${summe}</CtrlSum>`,
    `   <InitgPty><Nm>${name}</Nm></InitgPty>`,
    '  </GrpHdr>',
    '  <PmtInf>',
    `   <PmtInfId>${x(sepaText(a.nachrichtId, 33))}-1</PmtInfId>`,
    '   <PmtMtd>TRF</PmtMtd>',
    '   <BtchBookg>true</BtchBookg>',
    `   <NbOfTxs>${anzahl}</NbOfTxs>`,
    `   <CtrlSum>${summe}</CtrlSum>`,
    '   <PmtTpInf><SvcLvl><Cd>SEPA</Cd></SvcLvl></PmtTpInf>',
    v9 ? `   <ReqdExctnDt><Dt>${x(a.ausfuehrung)}</Dt></ReqdExctnDt>` : `   <ReqdExctnDt>${x(a.ausfuehrung)}</ReqdExctnDt>`,
    `   <Dbtr><Nm>${name}</Nm></Dbtr>`,
    `   <DbtrAcct><Id><IBAN>${ibanNormal(a.iban)}</IBAN></Id></DbtrAcct>`,
    `   <DbtrAgt>${bank(a.bic, true)}</DbtrAgt>`,
    '   <ChrgBr>SLEV</ChrgBr>',
  ]
  for (const p of a.posten) {
    const agent = bank(p.bic, false)
    zeilen.push(
      '   <CdtTrfTxInf>',
      `    <PmtId><EndToEndId>${x(sepaText(p.endToEndId, 35))}</EndToEndId></PmtId>`,
      `    <Amt><InstdAmt Ccy="EUR">${betragText(p.betrag)}</InstdAmt></Amt>`,
      ...(agent ? [`    <CdtrAgt>${agent}</CdtrAgt>`] : []),
      `    <Cdtr><Nm>${x(sepaText(p.empfaenger, 70))}</Nm></Cdtr>`,
      `    <CdtrAcct><Id><IBAN>${ibanNormal(p.iban)}</IBAN></Id></CdtrAcct>`,
      `    <RmtInf><Ustrd>${x(sepaText(p.verwendungszweck, 140))}</Ustrd></RmtInf>`,
      '   </CdtTrfTxInf>',
    )
  }
  zeilen.push('  </PmtInf>', ' </CstmrCdtTrfInitn>', '</Document>', '')
  return zeilen.join('\n')
}

// ── XML lesen (für camt.053) ────────────────────────────────────────────────

export interface XmlKnoten {
  name: string
  attr: Record<string, string>
  kinder: XmlKnoten[]
  text: string
}

const entitaet = (s: string) => s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&#(\d+);/g, (_, n: string) => String.fromCodePoint(Number(n))).replace(/&amp;/g, '&')

/**
 * Ein kleiner, strenger XML-Leser — genug für ISO-20022-Nachrichten: Elemente, Attribute, Text.
 * Namensraum-Präfixe fallen weg. Kein DTD, keine Entitäten außer den fünf festen: Ein Kontoauszug
 * braucht sie nicht, und ein Leser, der sie auflöst, ist eine Angriffsfläche.
 */
export function leseXml(xml: string): XmlKnoten {
  if (/<!DOCTYPE|<!ENTITY/i.test(xml)) throw new Error('XML_DOCTYPE')
  const wurzel: XmlKnoten = { name: '#', attr: {}, kinder: [], text: '' }
  const stapel = [wurzel]
  const teile = /<!--[\s\S]*?-->|<\?[\s\S]*?\?>|<!\[CDATA\[([\s\S]*?)\]\]>|<(\/?)([A-Za-z_][\w.:-]*)((?:\s+[\w.:-]+\s*=\s*(?:"[^"]*"|'[^']*'))*)\s*(\/?)>|([^<]+)/g
  let m: RegExpExecArray | null
  while ((m = teile.exec(xml))) {
    const oben = stapel[stapel.length - 1]
    if (m[1] !== undefined) oben.text += m[1]
    else if (m[3]) {
      const name = m[3].replace(/^.*:/, '')
      if (m[2]) {
        if (stapel.length < 2 || oben.name !== name) throw new Error('XML_MISMATCH')
        stapel.pop()
        continue
      }
      const attr: Record<string, string> = {}
      for (const a of (m[4] ?? '').matchAll(/([\w.:-]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g)) attr[a[1].replace(/^.*:/, '')] = entitaet(a[2] ?? a[3] ?? '')
      const k: XmlKnoten = { name, attr, kinder: [], text: '' }
      oben.kinder.push(k)
      if (!m[5]) stapel.push(k)
    } else if (m[6] !== undefined) oben.text += entitaet(m[6])
  }
  if (stapel.length !== 1 || !wurzel.kinder.length) throw new Error('XML_MISMATCH')
  return wurzel.kinder[0]
}

const kind = (k: XmlKnoten | undefined, ...pfad: string[]): XmlKnoten | undefined => pfad.reduce<XmlKnoten | undefined>((n, name) => n?.kinder.find((c) => c.name === name), k)
const alle = (k: XmlKnoten | undefined, name: string): XmlKnoten[] => k?.kinder.filter((c) => c.name === name) ?? []
const wert = (k: XmlKnoten | undefined, ...pfad: string[]): string | null => kind(k, ...pfad)?.text.trim() || null

// ── camt.053 ────────────────────────────────────────────────────────────────

export interface KontoBewegung {
  /** ISO-Buchungsdatum */
  datum: string | null
  betrag: number
  waehrung: string
  /** `DBIT` = Abgang (unsere Zahlung), `CRDT` = Eingang */
  richtung: 'DBIT' | 'CRDT'
  /** Storno bzw. Rückläufer */
  storno: boolean
  endToEndId: string | null
  /** Gegenseite */
  name: string | null
  iban: string | null
  zweck: string
}

/**
 * 12.4 — liest die Bewegungen eines `camt.053`-Auszugs. Eine Sammelbuchung (Batch) kommt als ein
 * Eintrag mit mehreren Einzelumsätzen; geliefert wird je Einzelumsatz eine Bewegung.
 */
export function leseCamt053(xml: string): KontoBewegung[] {
  const dok = leseXml(xml)
  const auszuege = alle(kind(dok, 'BkToCstmrStmt'), 'Stmt')
  if (dok.name !== 'Document' || !kind(dok, 'BkToCstmrStmt')) throw new Error('CAMT_FORMAT')
  const aus: KontoBewegung[] = []
  for (const stmt of auszuege) {
    for (const ntry of alle(stmt, 'Ntry')) {
      const richtung = wert(ntry, 'CdtDbtInd') === 'CRDT' ? 'CRDT' : 'DBIT'
      const storno = wert(ntry, 'RvslInd') === 'true'
      const datum = (wert(ntry, 'BookgDt', 'Dt') ?? wert(ntry, 'BookgDt', 'DtTm') ?? wert(ntry, 'ValDt', 'Dt'))?.slice(0, 10) ?? null
      const gesamt = kind(ntry, 'Amt')
      const einzel = alle(ntry, 'NtryDtls').flatMap((d) => alle(d, 'TxDtls'))
      const bewegung = (tx: XmlKnoten | undefined): KontoBewegung => {
        const betrag = kind(tx, 'Amt') ?? kind(tx, 'AmtDtls', 'TxAmt', 'Amt') ?? kind(tx, 'AmtDtls', 'InstdAmt', 'Amt') ?? gesamt
        // Gegenseite: bei einem Abgang der Empfänger, bei einem Eingang der Auftraggeber
        const seite = richtung === 'DBIT' ? 'Cdtr' : 'Dbtr'
        const partei = kind(tx, 'RltdPties', seite)
        const zweck = [...alle(kind(tx, 'RmtInf'), 'Ustrd').map((u) => u.text.trim()), wert(tx, 'RmtInf', 'Strd', 'CdtrRefInf', 'Ref') ?? '', tx ? '' : (wert(ntry, 'AddtlNtryInf') ?? '')].filter(Boolean).join(' ')
        const e2e = wert(tx, 'Refs', 'EndToEndId')
        return {
          datum,
          betrag: Number(betrag?.text.trim() ?? 0),
          waehrung: betrag?.attr.Ccy ?? gesamt?.attr.Ccy ?? 'EUR',
          richtung,
          storno,
          endToEndId: e2e && e2e !== 'NOTPROVIDED' ? e2e : null,
          name: wert(partei, 'Nm') ?? wert(partei, 'Pty', 'Nm'),
          iban: ibanNormal(wert(tx, 'RltdPties', `${seite}Acct`, 'Id', 'IBAN')) || null,
          zweck,
        }
      }
      if (einzel.length) for (const tx of einzel) aus.push(bewegung(tx))
      else aus.push(bewegung(undefined))
    }
  }
  return aus
}

// ── CSV-Kontoauszug bzw. OP-Liste (12.4) ────────────────────────────────────

export interface CsvZuordnung {
  delimiter: string
  /** Kopfzeile überspringen */
  hasHeader: boolean
  /** nullbasierte Spalten */
  columnReference: number
  columnDate: number
  columnAmount: number
  /** `TT.MM.JJJJ` oder `JJJJ-MM-TT` */
  dateFormat: 'TT.MM.JJJJ' | 'JJJJ-MM-TT'
  decimalSeparator: ',' | '.'
}

export interface CsvZahlung {
  zeile: number
  /** Eingangsnummer oder Rechnungsnummer des Lieferanten */
  referenz: string
  datum: string | null
  betrag: number
}

/** Eine CSV-Zeile in Felder — ein Trennzeichen in Anführungszeichen trennt nicht, `""` ist ein Anführungszeichen. */
function csvFelder(zeile: string, trenner: string): string[] {
  const aus: string[] = []
  let feld = ''
  let inText = false
  for (let i = 0; i < zeile.length; i++) {
    const z = zeile[i]
    if (inText) {
      if (z === '"' && zeile[i + 1] === '"') {
        feld += '"'
        i++
      } else if (z === '"') inText = false
      else feld += z
    } else if (z === '"' && !feld.trim()) {
      inText = true
      feld = ''
    } else if (zeile.startsWith(trenner, i)) {
      aus.push(feld.trim())
      feld = ''
      i += trenner.length - 1
    } else feld += z
  }
  aus.push(feld.trim())
  return aus
}

/** Liest Zahlungen aus einer CSV-Datei; Zeilen ohne Referenz oder ohne lesbaren Betrag fallen weg. */
export function leseZahlungsCsv(text: string, z: CsvZuordnung): CsvZahlung[] {
  // ein führendes BOM (U+FEFF) gehört nicht zum Inhalt
  const zeilen = (text.charCodeAt(0) === 0xfeff ? text.slice(1) : text).split(/\r?\n/)
  const aus: CsvZahlung[] = []
  for (const [i, roh] of zeilen.entries()) {
    if (!roh.trim() || (z.hasHeader && i === 0)) continue
    const felder = csvFelder(roh, z.delimiter)
    const referenz = felder[z.columnReference]?.trim() ?? ''
    const betragRoh = (felder[z.columnAmount] ?? '').replace(/[^\d,.\-]/g, '')
    const betrag = Number(z.decimalSeparator === ',' ? betragRoh.replace(/\./g, '').replace(',', '.') : betragRoh.replace(/,/g, ''))
    if (!referenz || !betragRoh || Number.isNaN(betrag) || betrag === 0) continue
    const d = felder[z.columnDate] ?? ''
    const tt = z.dateFormat === 'TT.MM.JJJJ' ? d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/) : null
    const datum = tt ? `${tt[3]}-${tt[2].padStart(2, '0')}-${tt[1].padStart(2, '0')}` : /^\d{4}-\d{2}-\d{2}/.test(d) ? d.slice(0, 10) : null
    aus.push({ zeile: i + 1, referenz, datum, betrag: Math.abs(betrag) })
  }
  return aus
}
